import ReactDOM from 'react-dom/client'
import React from 'react'
import { JewelryTypeName } from '../../trillion-common/src/typings/jewelry-type'
import { OnViewerConfigSaved } from './viewer/types/on-viewer-config-saved'
import { OnWidgetConfigSaved } from './viewer/types/on-widget-config-saved'
import { TrillionViewerWrap } from './trillion-viewer-wrap'
import { LoaderTheme } from './viewer/types/loader-theme'
import { CustomLoaderType } from './trillion-viewer'
import {
  changeBand,
  changeColor,
  setGemColor,
  changeCut,
  changeHalo,
  changeLetter,
  engrave,
} from './viewer/utils/ConfiguratorController'

export class TrillionViewerApp {
  private _root: ReactDOM.Root | null = null
  private _jewelryId: string | null = null
  private _zoomMax: number | undefined = undefined
  private _zoomMin: number | undefined = undefined
  private _startSize: number | undefined = undefined
  private _reducePixelRatio: boolean | undefined = undefined
  private _jewelryType: JewelryTypeName | null = null
  private _isGUIEnabled: boolean | undefined = undefined
  private _isPerformanceEnabled: boolean | undefined = undefined
  private _autoRotateSpeed: number | undefined = 0.6
  private _rotateObject: boolean | undefined = undefined
  private _rotateSpeed: number | undefined = undefined
  private _background: string | undefined = undefined
  private _onViewerConfigSaved: OnViewerConfigSaved | undefined = undefined
  private _onWidgetConfigSaved: OnWidgetConfigSaved | undefined = undefined
  private _customBackendUrl: string | null = null
  private _darkPointsOfView: boolean | undefined = undefined
  private _loaderTheme: LoaderTheme | null = null
  private _posteffectsConfigJson: string | undefined = undefined
  private _customLoader: CustomLoaderType | undefined = undefined
  private _serviceActivationKey: string | null = null
  private _disableShadowRoot = false
  private _cancelMirroring = false
  private _tryon = false
  private _useExternalConfigurator = false
  public configuratorController = {
    changeColor,
    setGemColor,
    engrave,
    changeCut,
    changeLetter,
    changeHalo,
    changeBand,
  }

  /**
   * Create viewer on the provided HTML element
   * @param entryElem - HTMLElement that the viewer will be loaded into
   */
  public init(entryElem: HTMLElement): void {
    if (this._root) {
      this.destroy()
    }
    this._root = ReactDOM.createRoot(entryElem)
  }

  /**
   * Set the id(SKU) of the jewelry to load
   * @param sku - Stock keeping unit
   */
  public setJewelryID(sku: string): void {
    this._jewelryId = sku
  }

  /**
   * Get the id(SKU) of the uploaded jewelry
   */
  public getJewelryID(): string | null {
    return this._jewelryId
  }

  /**
   * Set the type of jewelry to load
   * @param type - Jewelry type
   * @deprecated - jewelry type is now inferred from the jewelry id
   */
  public setJewelryType(type: JewelryTypeName | null): void {
    this._jewelryType = type
  }

  /**
   * Get the jewelry type
   * @deprecated - jewelry type is now inferred from the jewelry id
   */
  public getJewelryType(): JewelryTypeName | null {
    return this._jewelryType
  }

  public setIsGUIEnabled(isGUIEnabled: boolean | undefined): void {
    this._isGUIEnabled = isGUIEnabled
  }

  public getIsGUIEnabled(): boolean | undefined {
    return this._isGUIEnabled
  }

  public setIsPerformanceEnabled(isPerformanceEnabled: boolean | undefined): void {
    this._isPerformanceEnabled = isPerformanceEnabled
  }

  public getIsPerformanceEnabled(): boolean | undefined {
    return this._isPerformanceEnabled
  }

  public setAutoRotateSpeed(autoRotateSpeed: number | undefined): void {
    this._autoRotateSpeed = autoRotateSpeed
  }

  public setRotateObject(rotateObject: boolean | undefined): void {
    this._rotateObject = rotateObject
  }

  public getRotateObject(): boolean | undefined {
    return this._rotateObject
  }

  public setRotateSpeed(rotateSpeed: number | undefined): void {
    this._rotateSpeed = rotateSpeed
  }

  public getRotateSpees(): number | undefined {
    return this._rotateSpeed
  }

  public setBackground(background: string | undefined): void {
    this._background = background
  }

  public getAutoRotateSpeed(): number | undefined {
    return this._autoRotateSpeed
  }

  public setReducePixelRatio(reducePixelRatio: boolean | undefined): void {
    this._reducePixelRatio = reducePixelRatio
  }

  public getReducePixelRatio(): boolean | undefined {
    return this._reducePixelRatio
  }

  public setOnViewerConfigSaved(callback: OnViewerConfigSaved | undefined): void {
    this._onViewerConfigSaved = callback
  }

  public getOnViewerConfigSaved(): OnViewerConfigSaved | undefined {
    return this._onViewerConfigSaved
  }

  public setOnWidgetConfigSaved(callback: OnWidgetConfigSaved | undefined): void {
    this._onWidgetConfigSaved = callback
  }

  public getOnWidgetConfigSaved(): OnWidgetConfigSaved | undefined {
    return this._onWidgetConfigSaved
  }

  public setCustomBackendUrl(url: string | null): void {
    this._customBackendUrl = url
  }

  public getCustomBackendUrl(): string | null {
    return this._customBackendUrl
  }

  public setZoomMax(zoomMax: number | undefined): void {
    this._zoomMax = zoomMax
  }

  public setZoomMin(zoomMin: number | undefined): void {
    this._zoomMin = zoomMin
  }

  public setStartPosition(startSize: number | undefined): void {
    this._startSize = startSize
  }

  public setDarkPointsOfView(darkPointsOfView: boolean | undefined): void {
    this._darkPointsOfView = darkPointsOfView
  }

  public setLoaderTheme(theme: LoaderTheme | null): void {
    this._loaderTheme = theme
  }

  public setPosteffectsConfigJson(posteffectsConfigJson: string | undefined): void {
    this._posteffectsConfigJson = posteffectsConfigJson
  }

  public setCustomLoader(loader: CustomLoaderType | undefined): void {
    this._customLoader = loader
  }

  public setServiceActivationKey(key: string | null): void {
    this._serviceActivationKey = key
  }

  public getServiceActivationKey(): string | null {
    return this._serviceActivationKey
  }

  public setDisableShadowRoot(value: boolean): void {
    this._disableShadowRoot = value
  }

  public getDisableShadowRoot(): boolean {
    return this._disableShadowRoot
  }

  public setCancelMirroring(value: boolean): void {
    this._cancelMirroring = value
  }

  public setTryon(value: boolean): void {
    this._tryon = value
  }

  public useExternalConfigurator(value: boolean): void {
    this._useExternalConfigurator = value
  }

  /**
   * Restart the viewer after changing the parameters
   */
  public refresh(): void {
    if (!this._root) {
      throw Error('The viewer has not been initialized yet. Use TrillionViewerApp.init()')
    }

    this._root.render(
      <React.StrictMode>
        <TrillionViewerWrap
          key={this._jewelryId}
          jewelryId={this._jewelryId}
          jewelryType={this._jewelryType}
          isGUIEnabled={this._isGUIEnabled}
          autoRotateSpeed={this._autoRotateSpeed}
          reducePixelRatio={this._reducePixelRatio}
          rotateObject={this._rotateObject}
          rotateSpeed={this._rotateSpeed}
          isPerformanceEnabled={this._isPerformanceEnabled}
          background={this._background}
          onViewerConfigSaved={this._onViewerConfigSaved}
          onWidgetConfigSaved={this._onWidgetConfigSaved}
          customBackendUrl={this._customBackendUrl}
          zoomMax={this._zoomMax}
          zoomMin={this._zoomMin}
          startSize={this._startSize}
          darkPointsOfView={this._darkPointsOfView}
          loaderTheme={this._loaderTheme}
          posteffectsConfigJson={this._posteffectsConfigJson}
          customLoader={this._customLoader}
          serviceActivationKey={this._serviceActivationKey}
          disableShadowRoot={this._disableShadowRoot}
          cancelMirroring={this._cancelMirroring}
          tryon={this._tryon}
          useExternalConfigurator={this._useExternalConfigurator}
        />
      </React.StrictMode>,
    )
  }

  /**
   * Remove an existing viewer from the DOM
   */
  public destroy(): void {
    if (this._root) {
      if (typeof window.disposeAll === 'function') {
        window.disposeAll()
      }
      this._root.unmount()
      this._root = null
    }
  }
}
